import {
  type EquipmentAlias,
  type EquipmentBrand,
  useCreateBrandMutation,
  useGetManufacturersQuery,
  useUpdateBrandMutation,
} from '@/services/equipmentManufacturers';
import { isErrorWithData } from '@/services/helpers';
import { PalmettoButton, PalmettoSelect, PalmettoTextInput } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { EquipmentRecordStatus, ErrorCode } from 'types';

import { AliasEditor, normalizeAliases } from '../approved-vendor-list/AliasEditor';
import { EntityHistoryFeed } from '../shared/EntityHistoryFeed';

import { brandFieldLabel, brandPageLimit, formatEquipmentHistoryValue } from './equipmentLabels';

interface BrandFormValues {
  name: string;
  manufacturerId: string;
  websiteUrl: string;
  warrantyRegistrationUrl: string;
  aliases: EquipmentAlias[];
  note: string;
}

export interface EquipmentBrandFormProps {
  /** The manufacturer whose page this is; the default parent for a new brand. */
  manufacturerId: string;
  brand?: EquipmentBrand;
  onDone: () => void;
}

export const EquipmentBrandForm = ({ manufacturerId, brand, onDone }: EquipmentBrandFormProps) => {
  const isEdit = !!brand;

  const [createBrand, { isLoading: isCreating }] = useCreateBrandMutation();
  const [updateBrand, { isLoading: isUpdating }] = useUpdateBrandMutation();

  // Only an edit can re-map, and only to an active manufacturer, so the option list is fetched
  // only when one is open.
  const { data: manufacturersData } = useGetManufacturersQuery(
    isEdit ? { status: EquipmentRecordStatus.active, limit: brandPageLimit } : skipToken,
  );

  const defaultValues = useMemo<BrandFormValues>(
    () => ({
      name: brand?.name ?? '',
      manufacturerId: brand?.manufacturerId ?? manufacturerId,
      websiteUrl: brand?.websiteUrl ?? '',
      warrantyRegistrationUrl: brand?.warrantyRegistrationUrl ?? '',
      aliases: brand?.aliases?.length ? brand.aliases : [{ source: '', value: '' }],
      note: '',
    }),
    [brand, manufacturerId],
  );

  const { control, handleSubmit, setError } = useForm<BrandFormValues>({ defaultValues });

  const manufacturerOptions = useMemo(() => {
    const options = (manufacturersData?.data ?? []).map((manufacturer) => ({
      value: manufacturer.id,
      label: manufacturer.name,
    }));
    // A deactivated parent is absent from the active list; keep it selectable so the field is never
    // blank, and let the server reject the save with its own message.
    if (brand && !options.some((option) => option.value === brand.manufacturerId)) {
      options.unshift({ value: brand.manufacturerId, label: 'Current manufacturer' });
    }
    return options;
  }, [manufacturersData, brand]);

  const isLoading = isCreating || isUpdating;

  const onSubmit = useCallback(
    async (values: BrandFormValues) => {
      const name = values.name.trim();
      const websiteUrl = values.websiteUrl.trim();
      const warrantyRegistrationUrl = values.warrantyRegistrationUrl.trim();
      const aliases = normalizeAliases(values.aliases);

      try {
        if (brand) {
          await updateBrand({
            id: brand.id,
            previousManufacturerId: brand.manufacturerId,
            data: {
              manufacturerId: values.manufacturerId,
              name,
              aliases,
              // Null, not omitted: a partial `$set` leaves an absent key alone, so a URL the user
              // cleared would otherwise keep its old value.
              websiteUrl: websiteUrl || null,
              warrantyRegistrationUrl: warrantyRegistrationUrl || null,
              ...(values.note.trim() ? { note: values.note.trim() } : {}),
            },
          }).unwrap();
          toast.success('Brand updated');
        } else {
          await createBrand({
            manufacturerId,
            name,
            aliases,
            // Create has no stored value to clear, and its schema types both URLs as plain
            // strings, so a blank one is omitted rather than sent as null.
            ...(websiteUrl ? { websiteUrl } : {}),
            ...(warrantyRegistrationUrl ? { warrantyRegistrationUrl } : {}),
          }).unwrap();
          toast.success('Brand created');
        }
        onDone();
      } catch (error) {
        console.error('Error saving equipment brand:', error);
        if (!isErrorWithData(error)) {
          toast.error('Failed to save brand. Please try again.');
          return;
        }

        const { errorCode, message } = error.data;
        if (errorCode === ErrorCode.equipmentDuplicateName) {
          setError('name', { type: 'server', message });
        } else if (errorCode === ErrorCode.equipmentDuplicateAlias) {
          setError('aliases', { type: 'server', message });
        } else if (errorCode === ErrorCode.equipmentManufacturerInactive) {
          setError('manufacturerId', { type: 'server', message });
        } else {
          toast.error(message);
        }
      }
    },
    [brand, createBrand, updateBrand, manufacturerId, onDone, setError],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-16 bg-canvas-highlight-default p-16">
      <div className="grid grid-cols-2 gap-x-16 gap-y-8">
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field, fieldState }) => (
            <PalmettoTextInput
              id="brand-name"
              label="Name"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              validationMessage={
                fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
              }
              disabled={isLoading}
            />
          )}
        />
        {isEdit && (
          <Controller
            name="manufacturerId"
            control={control}
            render={({ field, fieldState }) => (
              <PalmettoSelect
                label="Manufacturer"
                helperText="Change this to re-map the brand to another manufacturer"
                items={manufacturerOptions.map((option) => ({
                  ...option,
                  selected: option.value === field.value,
                }))}
                onValueChange={(details: { value: string[] }) => field.onChange(details.value[0] ?? field.value)}
                validationMessage={
                  fieldState.error ? { type: 'error' as const, text: fieldState.error.message ?? '' } : undefined
                }
                disabled={isLoading}
              />
            )}
          />
        )}
        <Controller
          name="websiteUrl"
          control={control}
          render={({ field }) => (
            <PalmettoTextInput
              id="brand-website-url"
              label="Website"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={isLoading}
            />
          )}
        />
        <Controller
          name="warrantyRegistrationUrl"
          control={control}
          render={({ field }) => (
            <PalmettoTextInput
              id="brand-warranty-url"
              label="Warranty Registration"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={isLoading}
            />
          )}
        />
      </div>

      <Controller
        name="aliases"
        control={control}
        render={({ field, fieldState }) => (
          <AliasEditor
            value={field.value}
            onChange={field.onChange}
            disabled={isLoading}
            errorMessage={fieldState.error?.message}
          />
        )}
      />

      {isEdit && (
        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <PalmettoTextInput
              id="brand-note"
              label="Change Note"
              helperText="Optional note describing this change (recorded in history)"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={isLoading}
            />
          )}
        />
      )}

      {brand && (
        <details>
          <summary className="body-bold-300 text-text-primary">Change History</summary>
          <div className="mt-8">
            <EntityHistoryFeed
              history={brand.history ?? []}
              fieldLabel={brandFieldLabel}
              formatValue={formatEquipmentHistoryValue}
            />
          </div>
        </details>
      )}

      <div className="flex justify-end gap-8">
        <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={onDone} disabled={isLoading}>
          Cancel
        </PalmettoButton>
        <PalmettoButton emphasis="primary" layout="hug" disabled={isLoading}>
          {isEdit ? 'Save Brand' : 'Create Brand'}
        </PalmettoButton>
      </div>
    </form>
  );
};
